import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/DhanurvedaTerms.css';
import ShareableHeading from './ShareableHeading';

interface Props {
  onNavigate: (path: string) => void;
}

export default function DhanurvedaTerms({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const today = new Date().toLocaleDateString(locale === 'en' ? 'en-IN' : 'hi-IN', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  if (locale === 'en') {
    return (
      <div className="dv-terms-page">
        <button className="dv-terms-back" onClick={() => onNavigate('/rajdharma/dhanurveda')}>
          {t('dhanurveda.terms.back')}
        </button>

        <div className="dv-terms-header">
          <h1>{t('dhanurveda.terms.title')}</h1>
          <p>
            {t('dhanurveda.terms.subtitle')}<br />
            {t('dhanurveda.terms.location')}<br />
            {t('dhanurveda.terms.version', { today })}
          </p>
        </div>

        <div className="dv-terms-toc">
          <h3>{t('dhanurveda.terms.toc.title')}</h3>
          <ol>
            <li><a href="#parties">{t('dhanurveda.terms.toc.parties')}</a></li>
            <li><a href="#program">{t('dhanurveda.terms.toc.program')}</a></li>
            <li><a href="#eligibility">{t('dhanurveda.terms.toc.eligibility')}</a></li>
            <li><a href="#equipment">{t('dhanurveda.terms.toc.equipment')}</a></li>
            <li><a href="#booking">{t('dhanurveda.terms.toc.booking')}</a></li>
            <li><a href="#cancellation">{t('dhanurveda.terms.toc.cancellation')}</a></li>
            <li><a href="#risk">{t('dhanurveda.terms.toc.risk')}</a></li>
            <li><a href="#liability">{t('dhanurveda.terms.toc.liability')}</a></li>
            <li><a href="#conduct">{t('dhanurveda.terms.toc.conduct')}</a></li>
            <li><a href="#data">{t('dhanurveda.terms.toc.data')}</a></li>
            <li><a href="#force">{t('dhanurveda.terms.toc.force')}</a></li>
            <li><a href="#jurisdiction">{t('dhanurveda.terms.toc.jurisdiction')}</a></li>
            <li><a href="#grievance">{t('dhanurveda.terms.toc.grievance')}</a></li>
            <li><a href="#amendments">{t('dhanurveda.terms.toc.amendments')}</a></li>
          </ol>
        </div>

        <div className="dv-terms-section" id="parties">
          <ShareableHeading id="parties">{t('dhanurveda.terms.section.parties')}</ShareableHeading>
          <p>
            This agreement is made between <strong>Garuda XaaS Learning Portal</strong> ("the organization" / "we") and the participant ("you") under the <strong>Indian Contract Act, 1872</strong>.
          </p>
          <p>
            When you submit the advance booking form — whether through a digital signature or the "I agree" checkbox — this electronic acceptance forms a valid contract under <strong>Section 5 of the Information Technology Act, 2000</strong>.
          </p>
          <div className="dv-terms-highlight">
            <strong>{t('dhanurveda.terms.note.title')}</strong> {t('dhanurveda.terms.note.body')}
          </div>
        </div>

        <div className="dv-terms-section" id="program">
          <ShareableHeading id="program">{t('dhanurveda.terms.section.program')}</ShareableHeading>
          <p>
            <strong>Rajadharma discipline — Dhanurveda practice</strong> is a cultural and educational residential program focused on studying, preserving, and safely practicing the ancient Indian archery tradition.
          </p>
          <ul>
            <li>It is treated as a recreational sport and cultural education program.</li>
            <li>It includes Sanskrit reading based on the Mahabharata and Dhanurveda texts.</li>
            <li>It uses traditional wooden bows and bamboo arrows that do not fall within the definition of "arms" under the <strong>Arms Act, 1959</strong>.</li>
            <li>It is conducted only under direct supervision in a designated safe practice area.</li>
          </ul>
          <div className="dv-terms-highlight">
            This is not a competitive sport or military training program. It is not affiliated with <em>Archery India</em> or the <em>World Archery Federation</em>.
          </div>
        </div>

        <div className="dv-terms-section" id="eligibility">
          <ShareableHeading id="eligibility">{t('dhanurveda.terms.section.eligibility')}</ShareableHeading>
          <h3>3.1 Age limit</h3>
          <ul>
            <li>Minimum age: <strong>15 years</strong>.</li>
            <li>Participants aged 15–17 must submit a written guardian consent letter.</li>
            <li>There is no maximum age limit, but participants over 55 should provide a doctor-issued fit-for-sport certificate.</li>
          </ul>
          <h3>3.2 Health declaration</h3>
          <p>
            At booking, participants declare that they are not suffering from any uncontrolled heart condition, epilepsy, or severe spinal issue, and that they are fit for light physical activity. If they have any medical condition, they must inform the organization before registration and obtain medical approval where needed.
          </p>
          <div className="dv-terms-warning">
            <strong>Important:</strong> If a participant becomes unsafe to practice during the program, the organization may pause their participation. Partial refunds will follow Section 6.
          </div>
        </div>

        <div className="dv-terms-section" id="equipment">
          <ShareableHeading id="equipment">{t('dhanurveda.terms.section.equipment')}</ShareableHeading>
          <h3>4.1 Equipment provided</h3>
          <ul>
            <li>All bows, arrows, and safety gear (arm guard, finger tab) are provided by the organization.</li>
            <li>Participants may not bring their own archery equipment unless the organization gives written permission in advance.</li>
          </ul>
          <h3>4.2 Mandatory safety rules</h3>
          <ul>
            <li>Handle the bow only after the instructor's permission.</li>
            <li>Never release an arrow outside the target area.</li>
            <li>Follow the range hot/cold signal at all times.</li>
            <li>Never walk in front of or beside other participants while shooting is underway.</li>
            <li>Entry to the range under the influence of intoxicants is strictly prohibited.</li>
          </ul>
          <h3>4.3 Equipment damage</h3>
          <p>
            If equipment is damaged by deliberate action or gross negligence, the participant must pay the actual replacement cost. Normal wear and tear remains the organization's responsibility.
          </p>
        </div>

        <div className="dv-terms-section" id="booking">
          <ShareableHeading id="booking">{t('dhanurveda.terms.section.booking')}</ShareableHeading>
          <h3>5.1 Advance amount</h3>
          <ul>
            <li>An advance deposit of ₹500 is required to reserve a seat.</li>
            <li>The remaining amount is due <strong>7 working days before</strong> the program starts.</li>
            <li>If the balance is not received in time, the reservation may be cancelled and the advance will be refunded under Section 6.</li>
          </ul>
          <h3>5.2 Payment method</h3>
          <p>
            UPI / NEFT / IMPS — bank details will be sent in the confirmation email. Cash payments are not accepted.
          </p>
          <h3>5.3 Capacity</h3>
          <p>
            Each batch accepts a maximum of <strong>12 participants</strong>. A waitlist will be used if needed.
          </p>
          <h3>5.4 Confirmation</h3>
          <p>
            A confirmation email and booking reference number will be sent within 48 hours after payment is received. The email serves as proof of service under the <strong>Consumer Protection Act, 2019</strong>.
          </p>
        </div>

        <div className="dv-terms-section" id="cancellation">
          <ShareableHeading id="cancellation">{t('dhanurveda.terms.section.cancellation')}</ShareableHeading>
          <p>
            This policy follows the <strong>Consumer Protection Act, 2019</strong> and applicable government guidelines.
          </p>
          <div className="dv-terms-table-wrap">
            <table className="dv-terms-table">
              <thead>
                <tr>
                  <th>{t('dhanurveda.terms.table.time')}</th>
                  <th>{t('dhanurveda.terms.table.refund')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['30 days or more before the program', '100% (advance + balance)'],
                  ['15–29 days before', '75% (after admin costs)'],
                  ['7–14 days before', '50%'],
                  ['Less than 7 days before or no-show', 'No refund'],
                  ['Cancelled by the organization (flood/disaster etc.)', '100% refund'],
                  ['Medical emergency with certificate', '75% (any time)'],
                ].map(([time, refund]) => (
                  <tr key={time}>
                    <td>{time}</td>
                    <td className={refund.startsWith('100') ? 'dv-terms-table-success' : ''}>{refund}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="dv-terms-footnote">
            * Refunds are issued to the original payment method within 7–10 working days. One free reschedule is available up to 21 days before the program.
          </p>
        </div>

        <div className="dv-terms-section" id="risk">
          <ShareableHeading id="risk">{t('dhanurveda.terms.section.risk')}</ShareableHeading>
          <p>
            Participants acknowledge that archery practice carries natural risks that cannot be fully removed even with safety measures:
          </p>
          <ul>
            <li>Strain, sprain, or scratches on fingers and arms</li>
            <li>Sunlight or weather exposure in an open-air range</li>
            <li>Minor equipment-related incidents</li>
            <li>Natural mountain-area discomforts</li>
          </ul>
          <p>
            By completing the booking, participants voluntarily accept these risks and confirm that they are participating with full knowledge. This acceptance is valid under Section 14 of the Indian Contract Act, 1872 (free consent).
          </p>
          <div className="dv-terms-highlight">
            We strongly recommend that participants obtain personal accident insurance. The organization does not provide insurance coverage.
          </div>
        </div>

        <div className="dv-terms-section" id="liability">
          <ShareableHeading id="liability">{t('dhanurveda.terms.section.liability')}</ShareableHeading>
          <p>The organization's liability is limited as follows:</p>
          <ul>
            <li><strong>Maximum liability:</strong> Any claim is limited to the total fees paid by the participant.</li>
            <li>The organization is not responsible for the participant's own negligence, safety rule violations, unreported medical conditions, natural disasters, war, epidemic, government orders, theft, damage to personal belongings, or third-party accommodation services.</li>
          </ul>
          <div className="dv-terms-warning">
            This limitation does not apply in cases of unfair trade practice under Section 2(47) of the Consumer Protection Act, 2019. Participant rights remain protected.
          </div>
        </div>

        <div className="dv-terms-section" id="conduct">
          <ShareableHeading id="conduct">{t('dhanurveda.terms.section.conduct')}</ShareableHeading>
          <ul>
            <li>Respectful conduct is mandatory. Harassment, discrimination, and violence are not allowed.</li>
            <li>Alcohol, tobacco, and intoxicants are strictly prohibited on the program premises.</li>
            <li>Instructor instructions must be followed at all times.</li>
            <li>Any participant may be removed for misconduct without refund.</li>
            <li>Consent from other participants is required before taking photos or videos that include them (DPDP Act 2023).</li>
          </ul>
        </div>

        <div className="dv-terms-section" id="data">
          <ShareableHeading id="data">{t('dhanurveda.terms.section.data')}</ShareableHeading>
          <p>In compliance with the Digital Personal Data Protection Act, 2023:</p>
          <ul>
            <li><strong>Data use:</strong> The information in the booking form is used only for registration, contact, and emergency communication.</li>
            <li><strong>Retention:</strong> Data is permanently deleted 3 years after the program ends.</li>
            <li><strong>Third-party sharing:</strong> Data is not sold. It may be shared with medical responders in an emergency.</li>
            <li><strong>Your rights:</strong> Participants can request correction, erasure, and withdrawal of consent.</li>
            <li><strong>Complaint:</strong> For data-related complaints, contact <a className="dv-terms-link" href="mailto:contact@raajadharma.org">contact@raajadharma.org</a>.</li>
          </ul>
        </div>

        <div className="dv-terms-section" id="force">
          <ShareableHeading id="force">{t('dhanurveda.terms.section.force')}</ShareableHeading>
          <p>
            If the program must be cancelled because of natural disaster, earthquake, flood, heavy snow, epidemic, riot, government order, or another unforeseen event, the organization will provide a <strong>full refund</strong> or transfer the participant to the next available session, at the participant's choice.
          </p>
        </div>

        <div className="dv-terms-section" id="jurisdiction">
          <ShareableHeading id="jurisdiction">{t('dhanurveda.terms.section.jurisdiction')}</ShareableHeading>
          <p>This agreement is governed by <strong>Indian law</strong>.</p>
          <ul>
            <li><strong>Consumer forum:</strong> Participants may file complaints with the District Consumer Commission, Kangra, Himachal Pradesh under the Consumer Protection Act, 2019.</li>
            <li><strong>Arbitration:</strong> For disputes above ₹5,000, a single arbitrator in Dharamshala will resolve the matter under the Arbitration and Conciliation Act, 1996.</li>
            <li><strong>Court:</strong> If arbitration fails, the competent courts of Dharamshala, Himachal Pradesh have exclusive jurisdiction.</li>
          </ul>
        </div>

        <div className="dv-terms-section" id="grievance">
          <ShareableHeading id="grievance">{t('dhanurveda.terms.section.grievance')}</ShareableHeading>
          <p><strong>Grievance Officer:</strong></p>
          <div className="dv-terms-highlight">
            Name: Garuda XaaS Learning Portal — Grievance Cell<br />
            Email: <a className="dv-terms-link" href="mailto:contact@raajadharma.org">contact@raajadharma.org</a><br />
            Response time: within 30 days
          </div>
          <p>
            If the matter is not resolved within 30 days, you may file a complaint with the <a className="dv-terms-link" href="https://consumerhelpline.gov.in" target="_blank" rel="noopener noreferrer">National Consumer Helpline (consumerhelpline.gov.in)</a>.
          </p>
        </div>

        <div className="dv-terms-section" id="amendments">
          <ShareableHeading id="amendments">{t('dhanurveda.terms.section.amendments')}</ShareableHeading>
          <p>
            The organization reserves the right to revise these terms. The latest version applies to new registrations; existing participants remain governed by the version in effect at the time of booking.
          </p>
          <p>
            If any provision is declared invalid by a court, the remaining provisions continue in full force and effect (severability).
          </p>
          <p>
            These terms and conditions form the entire agreement between the parties and replace any prior oral or written discussions.
          </p>
        </div>

        <div className="dv-terms-footer">
          <p>
            <strong>Garuda XaaS Learning Portal</strong><br />
            Rajadharma discipline yoga — Dhanurveda practice center, Dharamshala, H.P.<br />
            Email: <a className="dv-terms-link" href="mailto:dhanurveda@raajadharma.org">dhanurveda@raajadharma.org</a>
          </p>
          <p className="dv-terms-copy">
            © {new Date().getFullYear()} Garuda XaaS. All rights reserved. The Hindi version remains the authoritative text.
          </p>
        </div>

        <div className="dv-terms-action">
          <button className="dv-terms-primary" onClick={() => onNavigate('/rajdharma/dhanurveda/booking')}>
            🏹 Book ahead
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-terms-page">
      <button
        className="dv-terms-back"
        onClick={() => onNavigate('/rajdharma/dhanurveda')}
      >
        {t('dhanurveda.terms.back')}
      </button>

      <div className="dv-terms-header">
        <h1>{t('dhanurveda.terms.title')}</h1>
        <p>
          {t('dhanurveda.terms.subtitle')}<br />
          {t('dhanurveda.terms.location')}<br />
          {t('dhanurveda.terms.version', { today })}
        </p>
      </div>

      {/* Table of Contents */}
      <div className="dv-terms-toc">
        <h3>{t('dhanurveda.terms.toc.title')}</h3>
        <ol>
          <li><a href="#parties">{t('dhanurveda.terms.toc.parties')}</a></li>
          <li><a href="#program">{t('dhanurveda.terms.toc.program')}</a></li>
          <li><a href="#eligibility">{t('dhanurveda.terms.toc.eligibility')}</a></li>
          <li><a href="#equipment">{t('dhanurveda.terms.toc.equipment')}</a></li>
          <li><a href="#booking">{t('dhanurveda.terms.toc.booking')}</a></li>
          <li><a href="#cancellation">{t('dhanurveda.terms.toc.cancellation')}</a></li>
          <li><a href="#risk">{t('dhanurveda.terms.toc.risk')}</a></li>
          <li><a href="#liability">{t('dhanurveda.terms.toc.liability')}</a></li>
          <li><a href="#conduct">{t('dhanurveda.terms.toc.conduct')}</a></li>
          <li><a href="#data">{t('dhanurveda.terms.toc.data')}</a></li>
          <li><a href="#force">{t('dhanurveda.terms.toc.force')}</a></li>
          <li><a href="#jurisdiction">{t('dhanurveda.terms.toc.jurisdiction')}</a></li>
          <li><a href="#grievance">{t('dhanurveda.terms.toc.grievance')}</a></li>
          <li><a href="#amendments">{t('dhanurveda.terms.toc.amendments')}</a></li>
        </ol>
      </div>

      {/* ── 1 ── */}
      <div className="dv-terms-section" id="parties">
        <ShareableHeading id="parties">{t('dhanurveda.terms.section.parties')}</ShareableHeading>
        <p>
          यह अनुबन्ध <strong>Garuda XaaS Learning Portal</strong> ("संस्था" / "हम") एवं
          कार्यक्रम में पंजीकरण करने वाले व्यक्ति ("प्रतिभागी" / "आप") के मध्य
          <strong> भारतीय संविदा अधिनियम, १८७२</strong> के अन्तर्गत निष्पादित होता है।
        </p>
        <p>
          अग्रिम बुकिंग फ़ॉर्म जमा करने पर — चाहे डिजिटल हस्ताक्षर हो अथवा "मैं सहमत
          हूँ" चेकबॉक्स — <strong>सूचना प्रौद्योगिकी अधिनियम, २०००</strong> की
          धारा ५ के अन्तर्गत यह इलेक्ट्रॉनिक स्वीकृति वैध अनुबन्ध का गठन करती है।
        </p>
        <div className="dv-terms-highlight">
          <strong>{t('dhanurveda.terms.note.title')}</strong> {t('dhanurveda.terms.note.body')}
        </div>
      </div>

      {/* ── 2 ── */}
      <div className="dv-terms-section" id="program">
        <ShareableHeading id="program">{t('dhanurveda.terms.section.program')}</ShareableHeading>
        <p>
          <strong>राजधर्मानुशासन योग — धनुर्वेद अभ्यास</strong> एक सांस्कृतिक-शैक्षणिक
          आवासीय कार्यक्रम है जिसका उद्देश्य भारत की प्राचीन धनुर्विद्या परम्परा का
          अध्ययन, संरक्षण एवं सौम्य अभ्यास करना है। यह कार्यक्रम:
        </p>
        <ul>
          <li>मनोरंजक खेल (Recreational Sport) एवं सांस्कृतिक शिक्षा की श्रेणी में आता है।</li>
          <li>महाभारत एवं धनुर्वेद ग्रन्थों पर आधारित संस्कृत पाठ से युक्त है।</li>
          <li>पारम्परिक काष्ठ-धनुष (wooden recurve bow) एवं बाँस-शर (bamboo arrow)
            का उपयोग करता है जो <strong>शस्त्र अधिनियम, १९५९</strong> के अन्तर्गत
            "शस्त्र" की परिभाषा में नहीं आते।</li>
          <li>अर्ध-योग्यता प्रशिक्षक की प्रत्यक्ष देखरेख में, निर्धारित सुरक्षित परिसर
            में ही आयोजित होता है।</li>
        </ul>
        <div className="dv-terms-highlight">
          यह कार्यक्रम प्रतियोगितात्मक खेल या सैन्य प्रशिक्षण नहीं है।
          इसे <em>Archery India</em> अथवा <em>World Archery Federation</em> द्वारा
          मान्यता प्राप्त नहीं है। यह विशुद्ध रूप से एक सांस्कृतिक पुनरुद्धार एवं
          व्यक्तित्व-विकास कार्यक्रम है।
        </div>
      </div>

      {/* ── 3 ── */}
      <div className="dv-terms-section" id="eligibility">
        <ShareableHeading id="eligibility">{t('dhanurveda.terms.section.eligibility')}</ShareableHeading>
        <h3>३.१ आयु सीमा</h3>
        <ul>
          <li>न्यूनतम आयु: <strong>१५ वर्ष</strong>।</li>
          <li>१५ से १७ वर्ष के प्रतिभागियों के लिए अभिभावक/संरक्षक का लिखित
            अनुमति-पत्र अनिवार्य है। बिना अनुमति-पत्र के प्रवेश नहीं।</li>
          <li>कोई अधिकतम आयु सीमा नहीं, परन्तु ५५ वर्ष से अधिक आयु के
            प्रतिभागियों को चिकित्सक का "फिट-फॉर-स्पोर्ट" प्रमाण-पत्र देना होगा।</li>
        </ul>
        <h3>३.२ स्वास्थ्य घोषणा</h3>
        <p>
          बुकिंग के समय प्रतिभागी घोषित करते हैं कि वे:
        </p>
        <ul>
          <li>किसी भी प्रकार की अनियन्त्रित हृदय-रोग, मिर्गी, या रीढ़ की गम्भीर
            समस्या से पीड़ित नहीं हैं।</li>
          <li>शारीरिक रूप से हल्के व्यायाम के लिए सक्षम हैं।</li>
          <li>यदि कोई चिकित्सकीय स्थिति है, तो पंजीकरण से पूर्व संस्था को सूचित
            करेंगे एवं योग्य चिकित्सक से अनुमोदन प्राप्त करेंगे।</li>
        </ul>
        <div className="dv-terms-warning">
          <strong>महत्त्वपूर्ण:</strong> यदि कार्यक्रम के दौरान किसी प्रतिभागी का
          स्वास्थ्य प्रतिभागिता हेतु असुरक्षित पाया जाए, तो संस्था को उन्हें
          अभ्यास से विरत करने का अधिकार है। ऐसी स्थिति में आंशिक धनवापसी
          धारा ६ के अनुसार होगी।
        </div>
      </div>

      {/* ── 4 ── */}
      <div className="dv-terms-section" id="equipment">
        <ShareableHeading id="equipment">{t('dhanurveda.terms.section.equipment')}</ShareableHeading>
        <h3>४.१ उपकरण प्रावधान</h3>
        <ul>
          <li>समस्त धनुष, बाण, एवं सुरक्षा उपकरण (arm guard, finger tab) संस्था
            द्वारा प्रदान किये जाएँगे।</li>
          <li>प्रतिभागी अपना धनुष-बाण लेकर नहीं आ सकते जब तक संस्था द्वारा
            पूर्व लिखित अनुमति न हो।</li>
        </ul>
        <h3>४.२ अनिवार्य सुरक्षा नियम</h3>
        <ul>
          <li>धनुष सदैव प्रशिक्षक की अनुमति के बाद ही उठाएँ।</li>
          <li>बाण कभी भी लक्ष्य-क्षेत्र के अतिरिक्त दिशा में न छोड़ें।</li>
          <li>Range Hot/Cold संकेत का पालन अनिवार्य।</li>
          <li>अन्य प्रतिभागियों के सामने या बाजू से कभी न जाएँ जब shooting हो।</li>
          <li>नशीले पदार्थों के प्रभाव में range पर प्रवेश पूर्णतः निषिद्ध।</li>
        </ul>
        <h3>४.३ उपकरण क्षति</h3>
        <p>
          जानबूझकर या घोर लापरवाही से उपकरण को क्षतिग्रस्त करने पर प्रतिभागी
          वास्तविक प्रतिस्थापन लागत का भुगतान करने के लिए उत्तरदायी होंगे।
          सामान्य उपयोग से होने वाली टूट-फूट संस्था की देनदारी है।
        </p>
      </div>

      {/* ── 5 ── */}
      <div className="dv-terms-section" id="booking">
        <ShareableHeading id="booking">{t('dhanurveda.terms.section.booking')}</ShareableHeading>
        <h3>५.१ अग्रिम राशि</h3>
        <ul>
          <li>स्थान आरक्षण हेतु ₹५०० की अग्रिम राशि देय है।</li>
          <li>शेष राशि कार्यक्रम प्रारम्भ से <strong>७ (सात) कार्य-दिवस पूर्व</strong> देय है।</li>
          <li>शेष राशि समय पर न आने पर आरक्षण स्वतः निरस्त हो सकता है एवं अग्रिम
            राशि अनुच्छेद ६ के अनुसार वापस होगी।</li>
        </ul>
        <h3>५.२ भुगतान माध्यम</h3>
        <p>
          UPI / NEFT / IMPS — संस्था के बैंक विवरण पुष्टि ईमेल में प्रेषित किए
          जाएँगे। कार्यक्रम में नकद भुगतान स्वीकार्य नहीं है।
        </p>
        <h3>५.३ पंजीकरण सीमा</h3>
        <p>
          प्रत्येक सत्र में अधिकतम <strong>१२ प्रतिभागी</strong> लिए जाते हैं।
          प्रतीक्षा-सूची की स्थिति में सूचित किया जाएगा।
        </p>
        <h3>५.४ पुष्टि</h3>
        <p>
          भुगतान प्राप्ति के ४८ घण्टे में पंजीकरण पुष्टि ईमेल एवं Booking Reference
          Number प्रेषित होगा। यह ईमेल <strong>उपभोक्ता संरक्षण अधिनियम, २०१९</strong>
          की धारा ७ के अन्तर्गत सेवा का प्रमाण है।
        </p>
      </div>

      {/* ── 6 ── */}
      <div className="dv-terms-section" id="cancellation">
        <ShareableHeading id="cancellation">{t('dhanurveda.terms.section.cancellation')}</ShareableHeading>
        <p>
          यह नीति <strong>उपभोक्ता संरक्षण अधिनियम, २०१९</strong> एवं
          <strong> MoCA दिशानिर्देशों</strong> के अनुरूप है।
        </p>
        <div className="dv-terms-table-wrap">
          <table className="dv-terms-table">
            <thead>
              <tr>
                <th>{t('dhanurveda.terms.table.time')}</th>
                <th>{t('dhanurveda.terms.table.refund')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['कार्यक्रम से ३० दिन या अधिक पूर्व', '१००% (अग्रिम + शेष)'],
                ['१५–२९ दिन पूर्व', '७५% (प्रशासनिक व्यय काटकर)'],
                ['७–१४ दिन पूर्व', '५०%'],
                ['७ दिन से कम पहले या अनुपस्थिति', 'कोई धनवापसी नहीं'],
                ['संस्था द्वारा रद्द (बाढ़/आपदा आदि)', '१००% धनवापसी'],
                ['चिकित्सकीय आपात — प्रमाण-पत्र सहित', '७५% (किसी भी समय)'],
              ].map(([time, refund]) => (
                <tr key={time}>
                  <td>{time}</td>
                  <td className={refund.startsWith('१००') ? 'dv-terms-table-success' : ''}>
                    {refund}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="dv-terms-footnote">
          * धनवापसी मूल भुगतान माध्यम में ७–१० कार्य-दिवस में होगी।
          पुनर्निर्धारण (एक बार) कार्यक्रम से २१ दिन पूर्व बिना शुल्क उपलब्ध है।
        </p>
      </div>

      {/* ── 7 ── */}
      <div className="dv-terms-section" id="risk">
        <ShareableHeading id="risk">{t('dhanurveda.terms.section.risk')}</ShareableHeading>
        <p>
          प्रतिभागी स्वीकार करते हैं कि धनुर्विद्या अभ्यास में निम्नलिखित स्वाभाविक
          जोखिम सम्भव हैं — जो सुरक्षा उपायों के बावजूद पूर्णतः समाप्त नहीं किए
          जा सकते:
        </p>
        <ul>
          <li>खींचाव (strain), मोच (sprain), या उँगलियों-भुजाओं पर खरोंच</li>
          <li>सूर्यप्रकाश या मौसमी प्रभाव (open-air range)</li>
          <li>उपकरण-विफलता से जुड़े minor incidents (अत्यन्त दुर्लभ)</li>
          <li>पर्वतीय क्षेत्र की प्राकृतिक असुविधाएँ</li>
        </ul>
        <p>
          बुकिंग पूर्ण करके प्रतिभागी स्वेच्छापूर्वक इन जोखिमों को स्वीकार करते हैं
          तथा घोषित करते हैं कि वे <strong>पूर्ण सूचना के साथ</strong> भाग ले रहे हैं।
          यह स्वीकृति भारतीय संविदा अधिनियम, १८७२ की धारा १४ (Free Consent) के
          अन्तर्गत मान्य है।
        </p>
        <div className="dv-terms-highlight">
          प्रतिभागियों को व्यक्तिगत दुर्घटना बीमा (<em>Personal Accident Insurance</em>)
          लेने की दृढ़तापूर्वक अनुशंसा की जाती है। संस्था कोई बीमा कवरेज प्रदान नहीं करती।
        </div>
      </div>

      {/* ── 8 ── */}
      <div className="dv-terms-section" id="liability">
        <ShareableHeading id="liability">{t('dhanurveda.terms.section.liability')}</ShareableHeading>
        <p>
          संस्था का दायित्व निम्नलिखित परिस्थितियों तक सीमित है:
        </p>
        <ul>
          <li>
            <strong>अधिकतम दायित्व:</strong> किसी भी दावे हेतु संस्था की अधिकतम
            देनदारी प्रतिभागी द्वारा भुगतान की गई कुल शुल्क-राशि तक सीमित है।
          </li>
          <li>
            संस्था निम्न के लिए उत्तरदायी नहीं होगी:
            <ul>
              <li>प्रतिभागी की अपनी लापरवाही, सुरक्षा नियम उल्लंघन, या असूचित
                चिकित्सकीय स्थिति से उत्पन्न क्षति।</li>
              <li>प्राकृतिक आपदा, युद्ध, महामारी, या सरकारी आदेश से बाधित
                कार्यक्रम।</li>
              <li>प्रतिभागी की व्यक्तिगत वस्तुओं की चोरी या क्षति।</li>
              <li>आवास-व्यवस्था (accommodation) जो तृतीय पक्ष द्वारा प्रदान की गई हो।</li>
            </ul>
          </li>
        </ul>
        <div className="dv-terms-warning">
          उपभोक्ता संरक्षण अधिनियम, २०१९ की धारा २(४७) के अन्तर्गत "अनुचित व्यापार
          व्यवहार" की स्थिति में यह सीमा लागू नहीं होगी। प्रतिभागियों के उपभोक्ता
          अधिकार सुरक्षित हैं।
        </div>
      </div>

      {/* ── 9 ── */}
      <div className="dv-terms-section" id="conduct">
        <ShareableHeading id="conduct">{t('dhanurveda.terms.section.conduct')}</ShareableHeading>
        <ul>
          <li>सहभागियों के साथ सम्मानजनक व्यवहार अनिवार्य। उत्पीड़न, भेदभाव, या
            हिंसा की किसी भी स्वरूप में अनुमति नहीं।</li>
          <li>कार्यक्रम स्थल पर मद्य, तम्बाकू, या किसी नशीले पदार्थ का सेवन
            पूर्णतः निषिद्ध।</li>
          <li>प्रशिक्षक के निर्देशों का पालन करना अनिवार्य है।</li>
          <li>किसी भी प्रतिभागी को आचार उल्लंघन पर बिना धनवापसी के कार्यक्रम से
            निष्कासित किया जा सकता है।</li>
          <li>सभी प्रतिभागियों द्वारा ली गई फ़ोटो/वीडियो में अन्य प्रतिभागियों की
            सहमति लेना आवश्यक है (DPDP Act 2023)।</li>
        </ul>
      </div>

      {/* ── 10 ── */}
      <div className="dv-terms-section" id="data">
        <ShareableHeading id="data">{t('dhanurveda.terms.section.data')}</ShareableHeading>
        <p>
          भारत के <strong>डिजिटल व्यक्तिगत डेटा संरक्षण अधिनियम, २०२३</strong> के
          अनुपालन में:
        </p>
        <ul>
          <li>
            <strong>डेटा का उपयोग:</strong> बुकिंग फ़ॉर्म में दी गई जानकारी केवल
            कार्यक्रम पंजीकरण, सम्पर्क, एवं आपात सूचना हेतु उपयोग होगी।
          </li>
          <li>
            <strong>डेटा प्रतिधारण:</strong> कार्यक्रम समाप्ति के ३ वर्ष बाद डेटा
            स्थायी रूप से हटा दिया जाएगा।
          </li>
          <li>
            <strong>तृतीय पक्ष साझाकरण:</strong> डेटा किसी तृतीय पक्ष को नहीं बेचा
            जाएगा। आपातकाल में चिकित्सा सेवा प्रदाताओं से साझा किया जा सकता है।
          </li>
          <li>
            <strong>आपके अधिकार:</strong> डेटा सुधार, डेटा विलोपन (Right to Erasure),
            एवं सहमति वापस लेने का अधिकार प्रतिभागी को प्राप्त है।
          </li>
          <li>
            <strong>शिकायत:</strong> डेटा-सम्बन्धी शिकायत हेतु{' '}
            <a className="dv-terms-link" href="mailto:contact@raajadharma.org">
                contact@raajadharma.org
            </a>{' '}
            पर सम्पर्क करें।
          </li>
        </ul>
      </div>

      {/* ── 11 ── */}
      <div className="dv-terms-section" id="force">
        <ShareableHeading id="force">{t('dhanurveda.terms.section.force')}</ShareableHeading>
        <p>
          प्राकृतिक आपदा, भूकम्प, बाढ़, बर्फ़बारी, महामारी, दंगा, सरकारी
          आदेश, या किसी अन्य अप्रत्याशित परिस्थिति में कार्यक्रम रद्द होने पर
          संस्था <strong>सम्पूर्ण भुगतान वापस</strong> करेगी अथवा प्रतिभागी की
          इच्छानुसार अगले सत्र में स्थानान्तरण करेगी।
        </p>
      </div>

      {/* ── 12 ── */}
      <div className="dv-terms-section" id="jurisdiction">
        <ShareableHeading id="jurisdiction">{t('dhanurveda.terms.section.jurisdiction')}</ShareableHeading>
        <p>
          यह अनुबन्ध <strong>भारतीय विधि</strong> के अन्तर्गत शासित होगा।
        </p>
        <ul>
          <li>
            <strong>उपभोक्ता मंच:</strong> उपभोक्ता संरक्षण अधिनियम, २०१९ के
            अन्तर्गत प्रतिभागी जिला उपभोक्ता आयोग (District Consumer Commission),
            कांगड़ा, हिमाचल प्रदेश में शिकायत दर्ज कर सकते हैं।
          </li>
          <li>
            <strong>मध्यस्थता:</strong> ₹५,०००/- से अधिक के विवादों में{' '}
            <em>मध्यस्थता एवं सुलह अधिनियम, १९९६</em> के अन्तर्गत धर्मशाला में
            एकल मध्यस्थ द्वारा समाधान।
          </li>
          <li>
            <strong>न्यायालय:</strong> यदि मध्यस्थता विफल हो — धर्मशाला,
            हिमाचल प्रदेश के सक्षम न्यायालय का अनन्य क्षेत्राधिकार होगा।
          </li>
        </ul>
      </div>

      {/* ── 13 ── */}
      <div className="dv-terms-section" id="grievance">
        <ShareableHeading id="grievance">{t('dhanurveda.terms.section.grievance')}</ShareableHeading>
        <p>
          <strong>शिकायत अधिकारी (Grievance Officer):</strong>
        </p>
        <div className="dv-terms-highlight">
          नाम: Garuda XaaS Learning Portal — शिकायत प्रकोष्ठ<br />
          ईमेल:{' '}
              <a className="dv-terms-link" href="mailto:contact@raajadharma.org">
            contact@raajadharma.org
          </a>
          <br />
          उत्तर-समय: ३० दिन के भीतर
        </div>
        <p>
          यदि ३० दिन में समाधान न हो तो आप{' '}
          <a
            className="dv-terms-link"
            href="https://consumerhelpline.gov.in"
            target="_blank"
            rel="noopener noreferrer"
          >
            National Consumer Helpline (consumerhelpline.gov.in)
          </a>{' '}
          पर शिकायत दर्ज कर सकते हैं।
        </p>
      </div>

      {/* ── 14 ── */}
      <div className="dv-terms-section" id="amendments">
        <ShareableHeading id="amendments">{t('dhanurveda.terms.section.amendments')}</ShareableHeading>
        <p>
          संस्था इन नियमों को संशोधित करने का अधिकार सुरक्षित रखती है। नए पंजीकरण
          पर नवीनतम संस्करण लागू होगा। पहले से पंजीकृत प्रतिभागियों पर उनकी बुकिंग
          के समय के नियम लागू होंगे।
        </p>
        <p>
          यदि कोई प्रावधान किसी न्यायालय द्वारा अमान्य घोषित हो, तो शेष प्रावधान
          पूर्ण बल में रहेंगे (Severability)।
        </p>
        <p>
          ये नियम एवं शर्तें पक्षकारों के मध्य सम्पूर्ण अनुबन्ध का गठन करती हैं
          और किसी भी पूर्ववर्ती मौखिक या लिखित वार्ता का स्थान लेती हैं।
        </p>
      </div>

      <div className="dv-terms-footer">
        <p>
          <strong>Garuda XaaS Learning Portal</strong><br />
          राजधर्मानुशासन योग — धनुर्वेद अभ्यास केन्द्र, धर्मशाला, हि.प्र.<br />
          ईमेल: <a className="dv-terms-link" href="mailto:dhanurveda@raajadharma.org">
            dhanurveda@raajadharma.org
          </a>
        </p>
        <p className="dv-terms-copy">
          © {new Date().getFullYear()} Garuda XaaS. सर्वाधिकार सुरक्षित।
          इन नियमों की हिन्दी भाषा अधिकृत पाठ है।
        </p>
      </div>

      <div className="dv-terms-action">
        <button
          className="dv-terms-primary"
          onClick={() => onNavigate('/rajdharma/dhanurveda/booking')}
        >
          🏹 अग्रिम बुकिंग करें
        </button>
      </div>
    </div>
  );
}
